import React, { useEffect, useState } from 'react';
import { getDatabase, ref, onValue } from 'firebase/database';
import { Link } from 'react-router-dom';
import styles from './OnlineUsersPage.module.css';

const USERS_PER_PAGE = 10;

const OnlineUsersPage = () => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const db = getDatabase();
    const usersRef = ref(db, 'onlineUsers');

    const unsubscribe = onValue(usersRef, (snapshot) => {
      const data = snapshot.val();
      const now = new Date();

      const filtered = Object.values(data || {}).filter((user) => {
        if (!user.lastActive) return false;
        const lastActive = new Date(user.lastActive);
        return now - lastActive < 2 * 60 * 1000; // active within 2 minutes
      });

      setOnlineUsers(filtered);
      setCurrentPage(1); // reset to first page on live update
    });

    return () => unsubscribe();
  }, []);

  const filteredUsers = onlineUsers.filter((user) =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const paginatedUsers = filteredUsers.slice(
    (currentPage - 1) * USERS_PER_PAGE,
    currentPage * USERS_PER_PAGE
  );

  const exportToCSV = () => {
    const headers = ['S. No.', 'Email', 'Last Active'];
    const rows = filteredUsers.map((user, index) => [
      index + 1,
      user.email,
      user.lastActive,
    ]);

    let csvContent = 'data:text/csv;charset=utf-8,';
    csvContent += headers.join(',') + '\n';
    rows.forEach((row) => {
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = 'online-users.csv';
    link.click();
  };

  const renderPagination = () => (
    <div className={styles.pagination}>
      <button
        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
        className={styles.pageButton}
        disabled={currentPage === 1}
      >
        ← Prev
      </button>

      <span className={styles.pageIndicator}>
        Page {currentPage} of {totalPages}
      </span>

      <button
        onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
        className={styles.pageButton}
        disabled={currentPage === totalPages}
      >
        Next →
      </button>
    </div>
  );

  return (
    <div className={styles.container}>
      <h2 className={styles.heading}>🟢 Online Users</h2>

      <div className={styles.controls}>
        <input
          type="text"
          placeholder="Search by email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
        />
        <div className={styles.buttonGroup}>
          <button onClick={exportToCSV} className={styles.exportBtn}>
            ⬇ Export CSV
          </button>
          <Link to="/admin" className={styles.backLink}>
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Top Pagination */}
      {paginatedUsers.length > 0 && renderPagination()}

      {paginatedUsers.length > 0 ? (
        <>
          <ul className={styles.userList}>
            {paginatedUsers.map((user, index) => (
              <li key={index} className={styles.userItem}>
                <div className={styles.topRow}>
                  <span className={styles.serial}>
                    {(currentPage - 1) * USERS_PER_PAGE + index + 1}.
                  </span>
                  <span className={styles.email}>{user.email}</span>
                </div>
                <div className={styles.bottomRow}>
                  <span className={styles.timestamp}>
                    {new Date(user.lastActive).toLocaleString()}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {/* Bottom Pagination */}
          {totalPages > 1 && renderPagination()}
        </>
      ) : (
        <p className={styles.noData}>No online users found.</p>
      )}
    </div>
  );
};

export default OnlineUsersPage;
